<template>
  <div class="auth-page people-workspace">
    <header class="people-heading">
      <div>
        <h1>实名认证</h1>
        <p>{{ query.status == null ? '全部认证申请' : statusText(query.status) }}<span class="heading-divider">/</span><strong>{{ loading ? '—' : total }}</strong> 条申请</p>
      </div>
      <a-button icon="download" @click="handleExport">导出认证名单</a-button>
    </header>
    <admin-filters class="page-toolbar" :active="query.status != null || !!query.batchNo">
      <div class="toolbar-left">
        <a-select v-model="query.status" allow-clear placeholder="全部认证状态" aria-label="认证状态" style="width:150px;" @change="search">
          <a-select-option :value="0">待审核</a-select-option>
          <a-select-option :value="1">已通过</a-select-option>
          <a-select-option :value="2">已驳回</a-select-option>
          <a-select-option :value="3">已撤销</a-select-option>
          <a-select-option :value="4">已过期</a-select-option>
        </a-select>
        <a-input v-model="query.batchNo" placeholder="邀请码（精确匹配）" aria-label="邀请码精确匹配" allow-clear style="width:210px;" @pressEnter="search" @change="query.batchNo || search()">
          <a-icon slot="prefix" type="search" />
        </a-input>
        <a-button type="primary" icon="search" @click="search">查询</a-button>
      </div>
      <div class="toolbar-right">
        <span class="review-order"><a-icon type="sort-ascending" /> 申请时间：较早优先</span>
        <a-button icon="reload" @click="loadData">刷新</a-button>
      </div>
    </admin-filters>

    <a-alert v-if="errorText" type="error" show-icon :message="errorText" style="margin-bottom:16px;" />
    <a-spin :spinning="loading">
      <a-empty v-if="!loading && !data.length" :description="errorText ? '暂时无法载入，请重试' : '暂无符合条件的认证申请'" class="empty-state" />

      <div v-else class="review-list">
        <article v-for="record in data" :key="record.id" class="review-card" :class="{ 'review-pending': record.status === 0 }">
          <section class="review-summary">
            <div class="summary-heading">
              <div>
                <div class="cell-name">{{ record.realName }}</div>
                <div class="cell-sub">{{ cardTypeText(record.idCardType) }}</div>
              </div>
              <a-tag :color="statusColor(record.status)">
                {{ statusText(record.status) }}
              </a-tag>
            </div>

            <dl class="summary-fields">
              <div>
                <dt>证件号码</dt>
                <dd>{{ maskIdNo(record.idCardNo) }}</dd>
              </div>
              <div>
                <dt>认证方式</dt>
                <dd>{{ record.verifyMethod === 'ocr' ? '自动识别' : '人工审核' }}</dd>
              </div>
              <div>
                <dt>申请提交时间</dt>
                <dd>{{ formatDateTime(record.createTime) }}</dd>
              </div>
              <div>
                <dt>证件有效期</dt>
                <dd>{{ expireDateText(record.idCardExpireAt) }}</dd>
              </div>
            </dl>
          </section>

          <section class="document-pair" aria-label="证件正反面">
            <div class="document-card">
              <div class="document-label">证件正面</div>
              <button v-if="record.frontAttachmentId" type="button" class="document-button" @click="openPreview(record, 'front')">
                <img v-if="documentUrl(record, 'front')" :src="documentUrl(record, 'front')" class="document-card-image" alt="证件正面" />
                <span v-else class="document-loading"><a-icon type="loading" /> 正在载入</span>
              </button>
              <div v-else class="document-missing">未提交正面</div>
            </div>

            <div class="document-card">
              <div class="document-label">证件反面</div>
              <button v-if="record.backAttachmentId" type="button" class="document-button" @click="openPreview(record, 'back')">
                <img v-if="documentUrl(record, 'back')" :src="documentUrl(record, 'back')" class="document-card-image" alt="证件反面" />
                <span v-else class="document-loading"><a-icon type="loading" /> 正在载入</span>
              </button>
              <div v-else class="document-missing">未提交反面</div>
            </div>
          </section>

          <aside class="review-actions">
            <a-button v-if="record.frontAttachmentId || record.backAttachmentId" icon="eye" @click="openPreview(record, 'front')">放大核对</a-button>
            <template v-if="record.status === 0">
              <a-button type="primary" icon="check" @click="review(record)">通过认证</a-button>
              <a-button type="danger" ghost icon="close" @click="openReject(record)">驳回申请</a-button>
            </template>
            <a-button v-if="record.status === 1" type="danger" ghost icon="stop" @click="openRevoke(record)">撤销认证</a-button>
            <a-button v-if="record.status !== 0" icon="file-text" @click="showDetail(record)">查看详情</a-button>
          </aside>
        </article>
      </div>

      <div v-if="total" class="pagination-bar">
        <a-pagination
          :current="query.page"
          :page-size="query.size"
          :total="total"
          :show-size-changer="true"
          :show-total="value => `共 ${value} 条`"
          @change="handlePageChange"
          @showSizeChange="handlePageSizeChange"
        />
      </div>
    </a-spin>

    <a-modal
      v-model="showPreviewModal"
      title="证件照片核对"
      :width="1100"
      :footer="null"
      :destroyOnClose="true"
      dialogClass="document-review-modal"
      @cancel="closePreview"
    >
      <div v-if="previewRecord" class="preview-workspace">
        <div class="preview-header">
          <div>
            <span class="preview-person">{{ previewRecord.realName }}</span>
            <span class="preview-type">{{ cardTypeText(previewRecord.idCardType) }}</span>
          </div>
          <div class="preview-id">证件号码：{{ previewRecord.idCardNo }} · 有效期：{{ expireDateText(previewRecord.idCardExpireAt) }}</div>
        </div>

        <div class="preview-document-grid">
          <section
            v-for="side in ['front', 'back']"
            :key="side"
            class="preview-document-panel"
            :class="{ 'is-focused': previewFocusSide === side }"
          >
            <div class="preview-toolbar">
              <strong>{{ side === 'front' ? '证件正面' : '证件反面' }}</strong>
              <div>
                <a-button size="small" icon="zoom-out" title="缩小" aria-label="缩小" :disabled="previewTransforms[side].zoom <= 0.5" @click="changeZoom(side, -0.25)" />
                <span class="zoom-value">{{ Math.round(previewTransforms[side].zoom * 100) }}%</span>
                <a-button size="small" icon="zoom-in" title="放大" aria-label="放大" :disabled="previewTransforms[side].zoom >= 4" @click="changeZoom(side, 0.25)" />
                <a-button size="small" icon="redo" title="顺时针旋转" aria-label="顺时针旋转" @click="rotatePreview(side)" />
                <a-button size="small" @click="resetPreview(side)">复位</a-button>
              </div>
            </div>
            <div class="preview-canvas">
              <img
                v-if="documentUrl(previewRecord, side)"
                :src="documentUrl(previewRecord, side)"
                :alt="side === 'front' ? '证件正面大图' : '证件反面大图'"
                :style="previewImageStyle(side)"
              />
              <div v-else class="document-missing preview-missing">暂无照片</div>
            </div>
          </section>
        </div>

        <div class="preview-review-bar">
          <span class="privacy-note"><a-icon type="safety-certificate" /> 仅用于本次实名认证审核，请勿截取或外传</span>
          <div v-if="previewRecord.status === 0" class="preview-review-actions">
            <a-button type="danger" ghost @click="rejectFromPreview">驳回申请</a-button>
            <a-button type="primary" icon="check" @click="approveFromPreview">通过认证</a-button>
          </div>
        </div>
      </div>
    </a-modal>

    <a-modal v-model="showReject" title="驳回原因" @ok="confirmReject" :confirmLoading="rejecting" width="420px">
      <a-input v-model="rejectForm.reason" placeholder="请填写驳回原因（将展示给运动员）" maxlength="200" />
    </a-modal>

    <a-modal
      v-model="showRevoke"
      title="撤销已通过认证"
      okText="确认撤销"
      okType="danger"
      :confirmLoading="revoking"
      width="480px"
      @ok="confirmRevoke"
    >
      <a-alert
        type="warning"
        show-icon
        message="撤销后用户将恢复为未认证，需重新提交资料。"
        description="原申请、通过时间和撤销原因会保留用于审计；证件原图会删除。"
        style="margin-bottom:16px;"
      />
      <a-textarea
        v-model="revokeForm.reason"
        :rows="4"
        :maxLength="500"
        placeholder="请填写撤销原因（必填，将展示给用户）"
      />
    </a-modal>

    <a-modal v-model="showDetailModal" title="认证详情" :footer="null" width="520px">
      <a-descriptions :column="1" bordered size="middle" v-if="detail">
        <a-descriptions-item label="真实姓名">{{ detail.realName }}</a-descriptions-item>
        <a-descriptions-item label="证件类型">{{ cardTypeText(detail.idCardType) }}</a-descriptions-item>
        <a-descriptions-item label="证件号码">{{ detail.idCardNo }}</a-descriptions-item>
        <a-descriptions-item label="认证方式">{{ detail.verifyMethod === 'ocr' ? '自动识别' : '人工审核' }}</a-descriptions-item>
        <a-descriptions-item label="证件有效期">{{ expireDateText(detail.idCardExpireAt) }}</a-descriptions-item>
        <a-descriptions-item label="邀请码">{{ detail.inviteCode || '—' }}</a-descriptions-item>
        <a-descriptions-item label="认证时间">{{ detail.verifiedAt || '—' }}</a-descriptions-item>
        <a-descriptions-item label="当前状态">{{ statusText(detail.status) }}</a-descriptions-item>
        <a-descriptions-item label="撤销原因" v-if="detail.status === 3">{{ detail.revokedReason || '—' }}</a-descriptions-item>
        <a-descriptions-item label="撤销时间" v-if="detail.status === 3">{{ formatDateTime(detail.revokedAt) }}</a-descriptions-item>
        <a-descriptions-item label="撤销操作人" v-if="detail.status === 3">{{ detail.revokedBy || '—' }}</a-descriptions-item>
        <a-descriptions-item label="过期判定时间" v-if="detail.status === 4">{{ formatDateTime(detail.expiredAt) }}</a-descriptions-item>
        <a-descriptions-item label="证件照片" v-if="documentUrl(detail, 'front') || documentUrl(detail, 'back')">
          <div style="display:flex; gap:10px;">
            <img v-if="documentUrl(detail, 'front')" :src="documentUrl(detail, 'front')" class="document-detail" @click="openPreview(detail, 'front')" title="正面" />
            <img v-if="documentUrl(detail, 'back')" :src="documentUrl(detail, 'back')" class="document-detail" @click="openPreview(detail, 'back')" title="反面" />
          </div>
        </a-descriptions-item>
        <a-descriptions-item label="驳回原因" v-if="detail.status === 2">{{ detail.rejectReason || '—' }}</a-descriptions-item>
      </a-descriptions>
    </a-modal>
  </div>
</template>

<script>
import { getAuthVerifications, reviewAuthVerification, revokeAuthVerification, exportAuthVerifications, getAuthVerificationFile } from '@/api'

export default {
    name: 'AuthVerifications',
    data() {
        return {
            data: [],
            total: 0,
            loading: false,
            errorText: '',
            loadSerial: 0,
            query: { status: 0, batchNo: '', page: 1, size: 20 },
            showReject: false,
            rejecting: false,
            rejectForm: { id: null, reason: '' },
            showRevoke: false,
            revoking: false,
            revokeForm: { id: null, reason: '' },
            showDetailModal: false,
            detail: null,
            documentUrls: {},
            showPreviewModal: false,
            previewRecord: null,
            previewFocusSide: 'front',
            previewTransforms: {
                front: { zoom: 1, rotation: 0 },
                back: { zoom: 1, rotation: 0 }
            }
        }
    },
    created() {
        this.loadData()
    },
    beforeDestroy() {
        this.loadSerial += 1
        this.clearDocumentUrls()
    },
    methods: {
        search() { this.query.page = 1; this.loadData() },
        loadData() {
            const serial = ++this.loadSerial
            this.clearDocumentUrls()
            this.loading = true
            this.errorText = ''
            getAuthVerifications({ ...this.query }).then(res => {
                if (serial !== this.loadSerial) return
                const data = res.data || {}
                this.data = data.list || []
                this.total = data.total || 0
                this.loadDocumentPreviews(this.data)
            }).catch(() => {
                if (serial === this.loadSerial) { this.data = []; this.total = 0; this.errorText = '认证申请载入失败，请点击刷新重试' }
            }).finally(() => { if (serial === this.loadSerial) this.loading = false })
        },
        documentUrl(record, side) {
            return record ? this.documentUrls[`${record.id}-${side}`] || '' : ''
        },
        loadDocumentPreviews(records) {
            records.forEach(record => {
                this.loadDocument(record, 'front', record.frontAttachmentId)
                this.loadDocument(record, 'back', record.backAttachmentId)
            })
        },
        loadDocument(record, side, attachmentId) {
            if (!attachmentId) return
            const serial = this.loadSerial
            getAuthVerificationFile(attachmentId).then(response => {
                if (serial !== this.loadSerial) return
                const url = URL.createObjectURL(response.data)
                this.$set(this.documentUrls, `${record.id}-${side}`, url)
            }).catch(() => {})
        },
        clearDocumentUrls() {
            Object.values(this.documentUrls).forEach(url => URL.revokeObjectURL(url))
            this.documentUrls = {}
        },
        handlePageChange(page) {
            this.query.page = page
            this.loadData()
        },
        handlePageSizeChange(current, size) {
            this.query.page = 1
            this.query.size = size
            this.loadData()
        },
        maskIdNo(value) {
            const idNo = String(value || '').trim()
            if (!idNo) return '—'
            if (idNo.length <= 8) return `${idNo.slice(0, 2)}****${idNo.slice(-2)}`
            return `${idNo.slice(0, 4)}********${idNo.slice(-4)}`
        },
        formatDateTime(value) {
            if (!value) return '—'
            return String(value).replace('T', ' ').split('.')[0]
        },
        expireDateText(value) {
            if (!value) return '—'
            return String(value) === '9999-12-31' ? '长期有效' : String(value)
        },
        statusText(status) {
            const map = { 0: '待审核', 1: '已通过', 2: '已驳回', 3: '已撤销', 4: '已过期' }
            return map[status] || '未知'
        },
        statusColor(status) {
            const map = { 0: 'orange', 1: 'green', 2: 'red', 3: '', 4: 'volcano' }
            return map[status] || ''
        },
        cardTypeText(type) {
            const map = { mainland: '内地身份证', hk_macau: '港澳回乡证', taiwan: '台胞证', passport: '护照', hkid: '香港身份证', macao: '澳门居民身份证', twid: '台湾身份证', other: '其他身份证件' }
            return map[type] || type || '—'
        },
        review(record) {
            this.$confirm({
                title: '确认通过认证？',
                content: `通过后 ${record.realName} 将获得认证运动员身份，请确认已核对证件正反面与填写资料。`,
                onOk: () => reviewAuthVerification({ verificationId: record.id, pass: true }).then(() => {
                    this.$message.success('已通过')
                    this.loadData()
                })
            })
        },
        openReject(record) {
            this.rejectForm = { id: record.id, reason: '' }
            this.showReject = true
        },
        confirmReject() {
            if (!this.rejectForm.reason.trim()) {
                this.$message.warning('请填写驳回原因')
                return
            }
            this.rejecting = true
            reviewAuthVerification({ verificationId: this.rejectForm.id, pass: false, reason: this.rejectForm.reason })
                .then(() => {
                    this.$message.success('已驳回')
                    this.showReject = false
                    this.loadData()
                }).finally(() => { this.rejecting = false })
        },
        openRevoke(record) {
            this.revokeForm = { id: record.id, reason: '' }
            this.showRevoke = true
        },
        confirmRevoke() {
            const reason = this.revokeForm.reason.trim()
            if (!reason) {
                this.$message.warning('请填写撤销原因')
                return
            }
            this.revoking = true
            revokeAuthVerification({ verificationId: this.revokeForm.id, reason })
                .then(() => {
                    this.$message.success('认证已撤销，用户可重新提交')
                    this.showRevoke = false
                    this.loadData()
                }).finally(() => { this.revoking = false })
        },
        openPreview(record, side) {
            this.previewRecord = record
            this.previewFocusSide = side || 'front'
            this.previewTransforms = {
                front: { zoom: 1, rotation: 0 },
                back: { zoom: 1, rotation: 0 }
            }
            this.showPreviewModal = true
        },
        closePreview() {
            this.showPreviewModal = false
            this.previewRecord = null
        },
        changeZoom(side, delta) {
            const current = this.previewTransforms[side]
            const zoom = Math.min(4, Math.max(0.5, Number((current.zoom + delta).toFixed(2))))
            this.$set(this.previewTransforms, side, { ...current, zoom })
        },
        rotatePreview(side) {
            const current = this.previewTransforms[side]
            this.$set(this.previewTransforms, side, { ...current, rotation: (current.rotation + 90) % 360 })
        },
        resetPreview(side) {
            this.$set(this.previewTransforms, side, { zoom: 1, rotation: 0 })
        },
        previewImageStyle(side) {
            const transform = this.previewTransforms[side]
            return {
                transform: `scale(${transform.zoom}) rotate(${transform.rotation}deg)`
            }
        },
        approveFromPreview() {
            const record = this.previewRecord
            this.closePreview()
            if (record) this.review(record)
        },
        rejectFromPreview() {
            const record = this.previewRecord
            this.closePreview()
            if (record) this.openReject(record)
        },
        showDetail(record) {
            this.detail = record
            this.showDetailModal = true
        },
        handleExport() {
            exportAuthVerifications({ batchNo: this.query.batchNo || undefined }).then(res => {
                const csv = res.data || ''
                if (!csv) {
                    this.$message.warning('暂无数据')
                    return
                }
                const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
                const url = URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = `认证运动员_${new Date().toISOString().slice(0, 10)}.csv`
                link.click()
                URL.revokeObjectURL(url)
            })
        }
    }
}
</script>

<style scoped>
.cell-name {
    font-weight: 600;
    font-size: 18px;
    line-height: 1.5;
}
.review-order { color: #526877; font-size: 14px; white-space: nowrap; }
.review-order .anticon { margin-right: 6px; }
.cell-sub {
    font-size: 12px;
    color: #6b7d8a;
}
.review-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-height: 160px;
}
.review-card {
    display: grid;
    grid-template-columns: minmax(190px, 0.75fr) minmax(440px, 1.8fr) 140px;
    gap: 20px;
    align-items: stretch;
    padding: 20px;
    background: #fff;
    border: 1px solid #e8eef3;
    border-radius: 12px;
    box-shadow: 0 2px 6px rgba(20, 56, 77, 0.025);
}
.review-card.review-pending { border-left: 3px solid #167d78; }
.review-summary {
    min-width: 0;
}
.summary-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding-bottom: 14px;
    border-bottom: 1px solid #f0f2f5;
}
.summary-fields {
    display: grid;
    gap: 10px;
    margin: 14px 0 0;
}
.summary-fields > div {
    min-width: 0;
}
.summary-fields dt {
    margin-bottom: 2px;
    color: #6b7d8a;
    font-size: 12px;
}
.summary-fields dd {
    margin: 0;
    color: #243746;
    font-size: 14px;
    line-height: 1.5;
    overflow-wrap: anywhere;
}
.document-pair {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
}
.document-card {
    min-width: 0;
}
.document-label {
    margin-bottom: 8px;
    color: #536675;
    font-size: 13px;
    font-weight: 600;
}
.document-button {
    display: block;
    width: 100%;
    padding: 0;
    overflow: hidden;
    background: #f7f9fb;
    border: 1px solid #dfe7ed;
    border-radius: 8px;
    cursor: zoom-in;
    transition: border-color .2s, box-shadow .2s;
}
.document-button:hover,
.document-button:focus {
    border-color: #4ecdc4;
    box-shadow: 0 0 0 3px rgba(78, 205, 196, 0.12);
    outline: none;
}
.document-card-image {
    display: block;
    width: 100%;
    height: 160px;
    object-fit: contain;
    background: #f4f6f8;
}
.document-loading,
.document-missing {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 160px;
    color: #9aa8b4;
    font-size: 13px;
    background: #f7f9fb;
    border: 1px dashed #d8e0e6;
    border-radius: 8px;
}
.document-loading {
    gap: 8px;
    border: 0;
}
.review-actions {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 10px;
    padding-left: 4px;
    border-left: 1px solid #f0f2f5;
}
.review-actions .ant-btn {
    width: 100%;
}
.pagination-bar {
    display: flex;
    justify-content: flex-end;
    margin-top: 18px;
    padding: 14px 16px;
    background: #fff;
    border-radius: 10px;
}
.empty-state {
    padding: 80px 0;
    background: #fff;
    border-radius: 12px;
}
.preview-workspace {
    color: #243746;
}
.preview-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin: -4px 0 16px;
    padding: 12px 16px;
    background: #f6faf9;
    border-radius: 8px;
}
.preview-person {
    margin-right: 10px;
    font-size: 18px;
    font-weight: 700;
}
.preview-type,
.preview-id {
    color: #687b89;
    font-size: 13px;
}
.preview-document-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
}
.preview-document-panel {
    overflow: hidden;
    border: 1px solid #dfe7ed;
    border-radius: 10px;
    transition: border-color .2s, box-shadow .2s;
}
.preview-document-panel.is-focused {
    border-color: #4ecdc4;
    box-shadow: 0 0 0 3px rgba(78, 205, 196, 0.1);
}
.preview-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    min-height: 46px;
    padding: 8px 10px 8px 14px;
    background: #f7f9fb;
    border-bottom: 1px solid #e8eef3;
}
.preview-toolbar .ant-btn {
    margin-left: 5px;
}
.zoom-value {
    display: inline-block;
    min-width: 48px;
    color: #687b89;
    font-size: 12px;
    text-align: center;
}
.preview-canvas {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 350px;
    overflow: auto;
    background-color: #eef2f4;
    background-image: linear-gradient(45deg, #e4e9ec 25%, transparent 25%), linear-gradient(-45deg, #e4e9ec 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e4e9ec 75%), linear-gradient(-45deg, transparent 75%, #e4e9ec 75%);
    background-position: 0 0, 0 8px, 8px -8px, -8px 0;
    background-size: 16px 16px;
}
.preview-canvas img {
    max-width: 92%;
    max-height: 92%;
    object-fit: contain;
    transform-origin: center;
    transition: transform .2s ease;
}
.preview-missing {
    width: calc(100% - 32px);
    height: calc(100% - 32px);
}
.preview-review-bar {
    position: sticky;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-top: 16px;
    padding: 14px 16px;
    background: #fff;
    border: 1px solid #e8eef3;
    border-radius: 8px;
    box-shadow: 0 -4px 14px rgba(20, 56, 77, 0.06);
}
.privacy-note {
    color: #728390;
    font-size: 13px;
}
.privacy-note .anticon {
    margin-right: 6px;
    color: #2fb6ac;
}
.preview-review-actions {
    display: flex;
    gap: 10px;
}
.document-detail {
    width: 210px;
    height: 140px;
    object-fit: contain;
    background: #f5f5f5;
    border-radius: 6px;
    cursor: pointer;
}
.page-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 12px;
  flex-wrap: wrap;
}
.toolbar-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
@media (max-width: 1180px) {
    .review-card {
        grid-template-columns: minmax(180px, .7fr) minmax(380px, 1.5fr);
    }
    .review-actions {
        grid-column: 1 / -1;
        flex-direction: row;
        justify-content: flex-end;
        padding-top: 14px;
        padding-left: 0;
        border-top: 1px solid #f0f2f5;
        border-left: 0;
    }
    .review-actions .ant-btn {
        width: auto;
    }
}
@media (max-width: 900px) {
    .review-card,
    .preview-document-grid {
        grid-template-columns: 1fr;
    }
    .document-card-image,
    .document-loading,
    .document-missing {
        height: 200px;
    }
    .preview-header,
    .preview-review-bar {
        align-items: flex-start;
        flex-direction: column;
    }
}
@media (max-width: 540px) {
    .review-card { padding: 16px; }
    .document-pair { grid-template-columns: 1fr; }
    .review-actions { flex-wrap: wrap; justify-content: flex-start; }
}
</style>
<style src="@/styles/people-workspace.css"></style>
